import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CompetitionDay,
  Shift,
  Volunteer,
  VolunteerExperience,
} from '@wht-xc-utils/shared/domain';
import { formatShiftTime, parseShiftTime } from '@wht-xc-utils/shared/domain';

import { EventTabs } from '../components/EventTabs';
import { api } from '../lib/api';

const EXPERIENCE_LEVELS: VolunteerExperience[] = [
  'unknown',
  'novice',
  'apprentice',
  'experienced',
  'expert',
];

interface AnalyzeResponse {
  filename: string;
  sheets: Array<{ sheetName: string; dayOrder: number; weekdayLabel: string; rowCount: number }>;
  days: CompetitionDay[];
  suggested: Record<string, string | null>;
}

interface ApplyResponse {
  summary: {
    filename: string;
    perSheetRowCount: Record<string, number>;
    daysProcessed: Array<{ sheetName: string; competitionDayId: string; date: string }>;
    sheetsSkipped: string[];
    volunteersUpserted: number;
    shiftsUpserted: number;
    shiftDefinitionsRead: number;
    warnings: string[];
  };
}

export function VolunteersPage() {
  const { eventId } = useParams<{ eventId: string }>();
  const qc = useQueryClient();

  const volunteers = useQuery({
    queryKey: ['volunteers', eventId],
    queryFn: () => api.get<{ volunteers: Volunteer[] }>(`/api/events/${eventId}/volunteers`),
    enabled: !!eventId,
  });
  const shifts = useQuery({
    queryKey: ['shifts', eventId],
    queryFn: () => api.get<{ shifts: Shift[] }>(`/api/events/${eventId}/shifts`),
    enabled: !!eventId,
  });
  const days = useQuery({
    queryKey: ['days', eventId],
    queryFn: () => api.get<{ days: CompetitionDay[] }>(`/api/events/${eventId}/days`),
    enabled: !!eventId,
  });

  const [analyze, setAnalyze] = useState<AnalyzeResponse | null>(null);
  const [mappings, setMappings] = useState<Record<string, string | null>>({});
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [importSummary, setImportSummary] = useState<ApplyResponse['summary'] | null>(null);

  const analyzeMutation = useMutation({
    mutationFn: async (file: File) => {
      const fd = new FormData();
      fd.append('file', file);
      return api.postForm<AnalyzeResponse>(
        `/api/events/${eventId}/volunteers/import-xlsx/analyze`,
        fd,
      );
    },
    onSuccess: (res) => {
      setAnalyze(res);
      setMappings(res.suggested);
      setImportSummary(null);
    },
  });

  const applyMutation = useMutation({
    mutationFn: async () => {
      if (!pendingFile) throw new Error('No file selected');
      const fd = new FormData();
      fd.append('file', pendingFile);
      fd.append('mappings', JSON.stringify(mappings));
      return api.postForm<ApplyResponse>(`/api/events/${eventId}/volunteers/import-xlsx/apply`, fd);
    },
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['volunteers', eventId] });
      qc.invalidateQueries({ queryKey: ['shifts', eventId] });
      qc.invalidateQueries({ queryKey: ['eventReadiness', eventId] });
      setImportSummary(res.summary);
      setAnalyze(null);
      setPendingFile(null);
    },
  });

  const deleteVolunteer = useMutation({
    mutationFn: (id: string) => api.delete(`/api/events/${eventId}/volunteers/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['volunteers', eventId] });
      qc.invalidateQueries({ queryKey: ['shifts', eventId] });
      qc.invalidateQueries({ queryKey: ['eventReadiness', eventId] });
    },
  });

  const updateVolunteer = useMutation({
    mutationFn: (input: { id: string; experience: VolunteerExperience }) =>
      api.patch<{ volunteer: Volunteer }>(`/api/events/${eventId}/volunteers/${input.id}`, {
        experience: input.experience,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['volunteers', eventId] }),
  });

  const shiftsByVolunteer = new Map<string, Shift[]>();
  for (const s of shifts.data?.shifts ?? []) {
    const list = shiftsByVolunteer.get(s.volunteerId) ?? [];
    list.push(s);
    shiftsByVolunteer.set(s.volunteerId, list);
  }
  const dayLabelById = new Map((days.data?.days ?? []).map((d) => [d.id, d.label || d.date]));
  const analyzedVolunteerRecords =
    analyze?.sheets.reduce((total, sheet) => total + sheet.rowCount, 0) ?? 0;

  return (
    <>
      <EventTabs />

      <div className="card">
        <h2>Import volunteer shifts (XLSX)</h2>
        {(days.data?.days ?? []).length === 0 ? (
          <p className="notice info">
            Add competition days on the <strong>Schedule</strong> tab first so XC days can be mapped
            to spreadsheet tabs.
          </p>
        ) : null}
        <div className="toolbar">
          <input
            type="file"
            accept=".xlsx"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              setPendingFile(f);
              setAnalyze(null);
              setImportSummary(null);
              if (f) analyzeMutation.mutate(f);
            }}
          />
          {analyzeMutation.isPending ? <span className="muted">Analyzing…</span> : null}
          {analyzeMutation.error ? (
            <span className="error">{(analyzeMutation.error as Error).message}</span>
          ) : null}
        </div>
        {analyze ? (
          <>
            <p className="muted" style={{ marginBottom: 6 }}>
              {analyze.filename}. Map sheets to competition days (only mapped sheets are imported).
            </p>
            {analyzedVolunteerRecords === 0 ? (
              <div className="notice warning">
                <strong>No volunteer records found.</strong> This file contains aggregate shift
                counts only. It can validate schedule coverage, but populating the volunteer list
                requires an export containing Last Name, First Name, eMail, and Phone columns.
              </div>
            ) : null}
            <table>
              <thead>
                <tr>
                  <th>Sheet</th>
                  <th>Weekday</th>
                  <th>Volunteer records</th>
                  <th>Competition day</th>
                </tr>
              </thead>
              <tbody>
                {analyze.sheets.map((s) => (
                  <tr key={s.sheetName}>
                    <td>{s.sheetName}</td>
                    <td>{s.weekdayLabel}</td>
                    <td>{s.rowCount}</td>
                    <td>
                      <select
                        value={mappings[s.sheetName] ?? ''}
                        onChange={(e) =>
                          setMappings({
                            ...mappings,
                            [s.sheetName]: e.target.value || null,
                          })
                        }
                      >
                        <option value="">(skip — not XC)</option>
                        {analyze.days.map((d) => (
                          <option key={d.id} value={d.id}>
                            {d.label || d.date} ({d.date})
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="toolbar" style={{ marginTop: 12 }}>
              <button
                className="primary"
                disabled={applyMutation.isPending}
                onClick={() => applyMutation.mutate()}
              >
                {applyMutation.isPending
                  ? 'Importing…'
                  : analyzedVolunteerRecords === 0
                    ? 'Validate mapped shifts'
                    : 'Import mapped sheets'}
              </button>
              {applyMutation.error ? (
                <span className="error">{(applyMutation.error as Error).message}</span>
              ) : null}
            </div>
          </>
        ) : null}
        {importSummary ? (
          <>
            <p className="notice success">
              Imported {importSummary.volunteersUpserted} volunteers /{' '}
              {importSummary.shiftsUpserted} shifts from {importSummary.daysProcessed.length}{' '}
              sheet(s); read {importSummary.shiftDefinitionsRead} distinct XC Jump Judge shift{' '}
              {importSummary.shiftDefinitionsRead === 1 ? 'definition' : 'definitions'}.
            </p>
            {importSummary.warnings.length > 0 ? (
              <div className="notice warning">
                <strong>Import warnings</strong>
                <ul>
                  {importSummary.warnings.map((warning) => (
                    <li key={warning}>{warning}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      <div className="card">
        <h2>Volunteers</h2>
        {volunteers.isLoading ? (
          <p className="muted">Loading…</p>
        ) : (volunteers.data?.volunteers.length ?? 0) === 0 ? (
          <p className="muted">No volunteers yet. Import a sheet above.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Last name</th>
                <th>First name</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Experience</th>
                <th>Shifts</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {(volunteers.data?.volunteers ?? []).map((v) => {
                const ss = shiftsByVolunteer.get(v.id) ?? [];
                return (
                  <tr key={v.id}>
                    <td>{v.lastName}</td>
                    <td>{v.firstName}</td>
                    <td>{v.email}</td>
                    <td>{v.phone}</td>
                    <td>
                      <select
                        value={v.experience}
                        onChange={(e) =>
                          updateVolunteer.mutate({
                            id: v.id,
                            experience: e.target.value as VolunteerExperience,
                          })
                        }
                      >
                        {EXPERIENCE_LEVELS.map((lvl) => (
                          <option key={lvl} value={lvl}>
                            {lvl}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      {ss.length === 0 ? (
                        <span className="muted">none</span>
                      ) : (
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {ss.map((s) => (
                            <li key={s.id}>
                              {dayLabelById.get(s.competitionDayId ?? '') ?? 'Unassigned day'}
                              {' • '}
                              {s.role}
                              {' • '}
                              {formatShiftTime(
                                parseShiftTime(s.shiftRaw || formatShiftTimeFromShift(s)),
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </td>
                    <td className="right">
                      <button
                        className="subtle danger"
                        onClick={() => {
                          if (confirm(`Delete ${v.firstName} ${v.lastName}? Shifts go too.`)) {
                            deleteVolunteer.mutate(v.id);
                          }
                        }}
                      >
                        ✕
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function formatShiftTimeFromShift(s: Shift): string {
  if (s.allDay) return 'All Day';
  if (s.shiftStartMin == null || s.shiftEndMin == null) return '';
  return `${minToHHMM(s.shiftStartMin)} to ${minToHHMM(s.shiftEndMin)}`;
}

function minToHHMM(min: number): string {
  const total = ((min % (24 * 60)) + 24 * 60) % (24 * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
}
